import { formatCurrency, formatDate, formatDateTime, formatNumber, isNumber, parseNumber, round } from 'intl-utils';
import { hide, show } from 'ui-utils';
import * as Vue from 'vue';

export function createApp(initialData = {}, components = {}) {
    // Merge the initial data, components and methods.
    const data = {
        ...initialData,
        ...components,
        ...{
            dispatch(name, argument, bubbles = true, e = event) {
                // Trigger the custom event.
                e.target.dispatchEvent(new CustomEvent(name, { bubbles: bubbles, detail: argument }));
            },
            formatCurrency,
            formatDate,
            formatDateTime,
            formatNumber,
            isNumber,
            mounted(element, data = this) {
                // Store the data against the element.
                this.setData(element, data);

                // Set that it has been mounted.
                element.$mounted = true;

                // Dispatch the mounted event.
                element.dispatchEvent(new CustomEvent('mounted', { detail: data }));
            },
            parseNumber,
            round,
            setData(element, data = this) {
                element.$data = data;
            },
            unmounted(element, data = this) {
                // Remove the data against the element.
                this.setData(element, null);

                // Set that it has been unmounted.
                element.$mounted = false;

                // Dispatch the unmounted event.
                element.dispatchEvent(new CustomEvent('unmounted', { detail: data }));
            }
        }
    };

    const app = Vue.createApp(data);

    // Register the custom directives.
    app.directive('kit-show', ctx => {
        const initialDisplay = ctx.el.style.display;

        ctx.effect(() => {
            const duration = ctx.arg ? (!isNaN(parseInt(ctx.arg)) ? parseInt(ctx.arg) : ctx.arg) : 400;

            // Note: The try/catch is used because sometimes jQuery throws an error (seems to happen when used within the edit view inside the list component, e.g. edit price band for a delivery method), therefore if an error is thrown then fallback to the default behaviour.
            if (ctx.get()) {
                try {
                    show(ctx.el, duration);
                } catch {
                    ctx.el.style.display = initialDisplay;
                }
            } else {
                try {
                    hide(ctx.el, duration);
                } catch {
                    ctx.el.style.display = 'none';
                }
            }
        });
    });

    return app;
}

export function mounted(element, listener) {
    if (element.$mounted === true)
        listener(element.$data);
    else
        element.addEventListener('mounted', e => listener(e.detail));
}

let hasRegisteredComponents = false, app, _components, _data;

/// <summary>
/// This should be called in a page's "pageshow" event handler as it makes sure data is mounted after the browsers bfcache.
/// </summary>
export function registerComponents(components = {}, data = {}) {
    // Make sure the components have not already been registered.
    if (hasRegisteredComponents)
        throw 'You have already registered the components.';

    // Create and mount the app (if applicable).
    app = document.querySelector('[v-scope]') ? createApp(data, components) : null;
    const vm = app?.mount();

    // Store the components and data as they are used within the event handler.
    _components = components;
    _data = data;

    // Add the content event listeners.
    document.addEventListener('DOMContentUpdating', contentUpdating);
    document.addEventListener('DOMContentUpdated', contentUpdated);

    // Set that the components have been registered.
    hasRegisteredComponents = true;

    return vm;
}

export function unregisterComponents() {
    // Make sure the components have been registered.
    if (!hasRegisteredComponents)
        return;

    // Unmount the app (if it has been mounted).
    app?.unmount();

    // Remove the content event handlers.
    document.removeEventListener('DOMContentUpdating', contentUpdating);
    document.removeEventListener('DOMContentUpdated', contentUpdated);

    // Set that the components have not been registered.
    hasRegisteredComponents = false;
}

const map = new Map();

function contentUpdating(e) {
    // Unmount the targetted element (if applicable). This fixes https://github.com/vuejs/vue-next/issues/2670.
    if (map.has(e.target))
        map.get(e.target).unmount();

    // Remove the mounted instance.
    map.delete(e.target);
}

function contentUpdated(e) {
    // Create and mount the app.
    const app = createApp(_data, _components);
    app.mount(e.target);

    // Store a reference to the mounted instance.
    map.set(e.target, app);
}

export function render(templateElement, data = {}, components = {}) {
    const element = document.createElement('div');
    element.innerHTML = templateElement.innerHTML;

    createApp(data, components).mount(element);
    
    return element.innerHTML;
}
